import { useEffect, useState, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { setActiveCompanyId } from '@/lib/company-context'
import type { Session } from '@supabase/supabase-js'

/**
 * Drop-in replacement for Clerk's useAuth() hook.
 * Provides isLoaded, isSignedIn, userId, getToken, and signOut.
 */
export function useAuth() {
  const [session, setSession] = useState<Session | null | undefined>(undefined)
  const queryClient = useQueryClient()

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
    })

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session)
    })

    return () => subscription.unsubscribe()
  }, [])

  const isLoaded = session !== undefined
  const isSignedIn = !!session
  const userId = session?.user?.id ?? null

  const getToken = useCallback(async () => {
    // Always fetch the live session from Supabase instead of relying on React state.
    // This avoids race conditions where oRPC requests fire before the session state updates.
    const { data: { session: currentSession } } = await supabase.auth.getSession()
    if (!currentSession) return null

    // If expired, refresh to get a new access_token
    // This is important for Capacitor/iOS where the app may be backgrounded for hours
    if (currentSession.expires_at && currentSession.expires_at * 1000 < Date.now()) {
      const { data } = await supabase.auth.refreshSession()
      return data.session?.access_token ?? null
    }
    return currentSession.access_token ?? null
  }, [])

  const signOut = useCallback(async () => {
    await setActiveCompanyId(null)
    setSession(null)
    await supabase.auth.signOut()
    queryClient.clear()
  }, [queryClient])

  return { isLoaded, isSignedIn, userId, getToken, signOut }
}
